import { useCallback, useState } from "react";
import type { Quote } from "@/api_client";
import { requiresMoreInformationToProceed } from "@/hooks/product_handlers/requiresMoreInformationToProceedFromEntry";
import { dataLayerPusher } from "@/web_analytics/dataLayer/dataLayerPusher/dataLayerPusher";
import { findQuote } from "../../utils/productUtils";
import { useAddProduct } from "../api/product/useAddProduct";
import { useProductNavigationHandlers } from "../useProductNavigationHandlers";

type ProductNavigationHook = typeof useProductNavigationHandlers;

export const useSubmitEntryHandler = <T extends Quote>(
  navigationHook: ProductNavigationHook = useProductNavigationHandlers,
) => {
  const productNavigation = navigationHook();
  const addProduct = useAddProduct();
  const [requiresMoreInformation, setRequiresMoreInformation] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);

  const resetSubmitStates = useCallback(() => {
    setRequiresMoreInformation(false);
    addProduct.reset();
  }, [addProduct]);

  const submitEntryHandler = async (product: T) => {
    if (addProduct.isPending) {
      return;
    }

    dataLayerPusher.entryFormClick();
    const addedProduct = await addProduct.mutateAsync(product);

    const foundQuote = findQuote(addedProduct.offer, addedProduct?.id);
    const requiresMoreInfo = requiresMoreInformationToProceed(
      product.productType,
      foundQuote,
    );

    if (requiresMoreInfo) {
      setRequiresMoreInformation(true);
      return;
    }

    if (
      foundQuote?.priceCalculatedAt !== undefined &&
      foundQuote.priceCalculatedAt !== null
    ) {
      setIsNavigating(true);
      productNavigation.goToPricePage(product.productType, addedProduct?.id);
      return;
    }

    setIsNavigating(true);
    productNavigation.goToEditProduct(product.productType, addedProduct?.id);
  };

  return {
    submitEntryHandler,
    isPending: addProduct.isPending || isNavigating,
    success: addProduct.isSuccess && !requiresMoreInformation,
    requiresMoreInformation,
    resetSubmitStates,
  };
};
